<template>
    <v-list nav dense>
        <template v-if="level === 'instance' && primaryInstanceOptions.length">
            <v-list-item
                v-for="option in primaryInstanceOptions"
                :key="`primary-instance-${option.id}`"
                :input-value="isSelected(option.id)"
                active-class="primary--text"
                @click="$emit('select', option.data)">
                <v-list-item-icon>
                    <v-icon>{{ getInstanceTypeIcon(option.text) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title :class="{ 'text--disabled': isInactiveSpace }">
                        <b v-if="option.data === masterInstance">{{ option.text }}</b>
                        <template v-else>{{ option.text }}</template>
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
        </template>

        <template v-if="level === 'snapshot' && currentStateOption">
            <v-list-item
                :input-value="isSelected(currentStateOption.id)"
                class="primary--text"
                active-class="primary--text"
                @click="$emit('select', currentStateOption.data)">
                <v-list-item-icon>
                    <v-icon color="primary">mdi-record-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title :class="{ 'text--disabled': isInactiveSpace }">
                        <b>{{ currentStateOption.text }}</b>
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
        </template>

        <v-list-item class="px-0">
            <v-list-item-title>
                <v-text-field
                    v-model="search"
                    :placeholder="searchPlaceholder"
                    prepend-inner-icon="mdi-magnify"
                    background-color="grey lighten-4"
                    autofocus
                    hide-details
                    solo
                    flat
                    dense>
                    <template v-if="level === 'space'" v-slot:append>
                        <SpaceTypeFilterMenu v-model="selectedSpaceTypes" />
                    </template>
                </v-text-field>
            </v-list-item-title>
        </v-list-item>

        <v-list-item>
            <v-list-item-title class="text-uppercase">{{ level }}s</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="loading">
            <v-list-item-content>
                <v-progress-linear indeterminate rounded></v-progress-linear>
            </v-list-item-content>
        </v-list-item>

        <template v-else-if="visibleOptions.length">
            <v-list-item
                v-for="option in visibleOptions"
                :key="`${level}-${option.id}`"
                :input-value="isSelected(option.id)"
                :disabled="option.disabled"
                active-class="primary--text"
                @click="$emit('select', option.data)">
                <v-btn
                    v-if="level === 'space'"
                    fab
                    x-small
                    elevation="0"
                    class="mr-4 flex-shrink-0"
                    :color="spaceTypeButtonColor(option.data)"
                    tabindex="-1"
                    style="pointer-events: none">
                    <v-icon small :class="spaceTypeIconColorClass(option.data)">{{ spaceTypeIcon(option.data) }}</v-icon>
                </v-btn>
                <v-list-item-icon v-if="level === 'snapshot'">
                    <v-icon :color="dotColor(option.data)">mdi-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title :class="{ 'text--disabled': option.muted }">{{ option.text }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="option.role">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">{{ option.role.icon }}</v-icon>
                        </template>
                        <span>{{ option.role.label }}</span>
                    </v-tooltip>
                </v-list-item-action>
            </v-list-item>
        </template>

        <v-list-item v-else>
            <v-list-item-content>
                <v-list-item-title class="text--disabled text-center">No matching {{ level }}s found.</v-list-item-title>
            </v-list-item-content>
        </v-list-item>

        <template v-if="level !== 'organization'">
            <v-divider class="my-2"></v-divider>
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-btn block color="grey lighten-4" @click="$emit('view-all')">View all {{ level }}s</v-btn>
                </v-list-item-title>
            </v-list-item>
        </template>
    </v-list>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { getInstanceTypeIcon, instanceRole, isCurrentState, organizationRole, spaceRole } from '@/utils'
import { SPACE_TYPE_ORDER } from '@/utils/spaceTypeMeta'
import spaceTypeUi from '@/mixins/spaceTypeUi'
import { snapshotEnums } from '@/mixins/snapshot'
import { enumsData } from '@/mixins/enums'
import SpaceTypeFilterMenu from '@/components/SpaceTypeFilterMenu.vue'

export default {
    name: 'ContextSelectorList',
    components: { SpaceTypeFilterMenu },
    mixins: [enumsData, spaceTypeUi, snapshotEnums],
    props: {
        level: { type: String, required: true },
        open: { type: Boolean, default: false },
        selectedId: { type: [String, Number], default: null }
    },
    data() {
        return { search: '', selectedSpaceTypes: SPACE_TYPE_ORDER.slice() }
    },
    computed: {
        ...mapState(['userOrgs', 'fetchingOrgs']),
        ...mapState('orgStore', ['educationSpaces', 'researchSpaces', 'datasetSpaces', 'spacesWithPrimarySnapshots', 'fetchingSpaces']),
        ...mapState('spaceStore', ['masterInstance', 'distributedInstance', 'otherInstances', 'fetchingInstances']),
        ...mapState('instanceStore', ['instanceSnapshots', 'fetchingSnapshots']),
        ...mapGetters('spaceStore', ['currentSpaceType', 'isSpaceArchived', 'isSpaceRested']),
        ...mapGetters('instanceStore', { currentInstanceIsEditor: 'isInstanceEditor', currentInstanceIsDistributed: 'isDistributedInstance' }),
        loading() {
            return { organization: this.fetchingOrgs, space: this.fetchingSpaces, instance: this.fetchingInstances, snapshot: this.fetchingSnapshots }[
                this.level
            ]
        },
        isInactiveSpace() {
            return Boolean(this.isSpaceArchived || this.isSpaceRested)
        },
        searchPlaceholder() {
            return this.level === 'space' ? 'Filter all spaces...' : `Filter ${this.level}s...`
        },
        primaryInstanceOptions() {
            const instances = []
            if (this.masterInstance) instances.push(this.masterInstance)
            if (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE && this.distributedInstance) instances.push(this.distributedInstance)
            return instances.map(instance => ({ id: instance.iid, text: instance.long_id, data: instance }))
        },
        currentStateOption() {
            if (!this.currentInstanceIsEditor || this.currentInstanceIsDistributed) return null
            const snapshot = this.instanceSnapshots.find(item => isCurrentState(item.short_id))
            if (!snapshot) return null
            return { id: snapshot.snid, text: this.snapshotName(snapshot), data: snapshot }
        },
        options() {
            if (this.level === 'organization') {
                return [...this.userOrgs]
                    .sort((a, b) => a.long_id.localeCompare(b.long_id, undefined, { sensitivity: 'base' }))
                    .map(org => ({ id: org.oid, text: org.long_id, role: organizationRole(org.role), data: org }))
            }
            if (this.level === 'space') {
                return [...this.spacesWithPrimarySnapshots]
                    .filter(space => !space.space_archival_timestamp && !space.space_rested_timestamp)
                    .sort((a, b) => new Date(b.space_creation_timestamp || 0) - new Date(a.space_creation_timestamp || 0))
                    .map(space => ({
                        id: space.sid,
                        text: space.space_long_id,
                        role: spaceRole(space.space_role),
                        spaceTypeKey: this.getSpaceTypeKey(space),
                        data: space
                    }))
            }
            if (this.level === 'instance') {
                return [...this.otherInstances]
                    .sort((a, b) => new Date(b.creation_timestamp || 0) - new Date(a.creation_timestamp || 0))
                    .map(instance => ({
                        id: instance.iid,
                        text: instance.long_id,
                        role: instanceRole(instance.role),
                        disabled: Array.isArray(instance.data) && instance.data.length === 0,
                        muted: this.isInactiveSpace,
                        data: instance
                    }))
            }
            return [...this.instanceSnapshots]
                .filter(snapshot => !isCurrentState(snapshot.short_id))
                .sort((a, b) => new Date(b.snapshot_timestamp || 0) - new Date(a.snapshot_timestamp || 0) || Number(b.snid) - Number(a.snid))
                .map(snapshot => ({ id: snapshot.snid, text: this.snapshotName(snapshot), muted: this.isInactiveSpace, data: snapshot }))
        },
        visibleOptions() {
            const search = this.search.trim().toLowerCase()
            const options = this.options.filter(option => {
                const matchesSearch = option.text.toLowerCase().includes(search)
                const matchesSpaceType = this.level !== 'space' || this.selectedSpaceTypes.includes(option.spaceTypeKey)
                return matchesSearch && matchesSpaceType
            })
            if (this.level === 'space' || (!search && ['instance', 'snapshot'].includes(this.level))) return options.slice(0, 5)
            return options
        }
    },
    methods: {
        getInstanceTypeIcon,
        isSelected(id) {
            return this.selectedId !== null && this.selectedId !== undefined && this.selectedId.toString() === id.toString()
        },
        snapshotName(snapshot) {
            if (isCurrentState(snapshot.short_id)) {
                if (this.isSpaceArchived) return 'Archived'
                if (this.isSpaceRested) return 'Resting'
            }
            return snapshot.long_id
        }
    },
    watch: {
        open(isOpen) {
            if (isOpen) this.search = ''
        }
    }
}
</script>
